import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDecimal, formatInteger } from '@/lib/language';
import { useChapterOpen } from '@/hooks/optimizationScore/chapterOpenPreference';
import type { PriceBenchmarkSummary } from '@/hooks/usePriceBenchmarkSummary';

export interface FeedCheckChaptersProps {
  feedId: string | null;
  /** `usePriceBenchmarkSummary().data` — null heisst "kein Benchmark fuer diesen Feed". */
  benchmark: PriceBenchmarkSummary | null;
  /** Wird nur gerendert, wenn offen. Der Aufrufer reicht die fertige Section
   *  herein, damit diese Komponente `PriceBenchmarkSection` nicht importieren
   *  muss (die Section haengt an weiteren Props, die hier nicht bekannt sind). */
  priceSection?: React.ReactNode;
  /** Die fertige Ads-Zeile (`AdsChapterRow`), von der Seite hereingereicht wie
   *  die Preis-Section. Ohne sie entfaellt die Zeile komplett. */
  adsRow?: React.ReactNode;
  /** Die fertige Zeile des Kapitels "Bei Google" (`LiveChapterRow`, Spec
   *  2026-09-16 Abschnitt 6), hereingereicht wie `adsRow`: diese Karte muss
   *  damit weder `useLiveFeedStatus` noch den Upload-Pfad kennen. Ohne sie
   *  entfaellt die Zeile komplett. */
  liveRow?: React.ReactNode;
  /** Stellung und Gesamtzahl der Kapitel, aus derselben Rechnung wie die Nummer
   *  der Ads-Zeile. Die Preis-Zeile traegt ihre Nummer NUR mit Benchmark: ohne
   *  ihn ist sie kein Kapitel (sie zaehlt nicht mit) und stand trotzdem als
   *  "Kapitel 2" ueber einer Ads-Zeile, die dann ebenfalls Kapitel 2 ist.
   *  Fehlt eine der beiden Zahlen, steht dort das blosse Wort -- lieber keine
   *  Nummer als eine falsche. */
  priceChapterIndex?: number;
  chapterTotal?: number;
  /** A2-NEU-2: die Benchmark-Abfrage ist gescheitert (und es liegt kein
   *  frueherer Stand vor). Statt des Leersatzes steht dann eine Statuszeile. */
  benchmarkFailed?: boolean;
  /** Wiederholt die Benchmark-Abfrage; ohne sie entfaellt der Knopf. */
  onRetryBenchmark?: () => void;
}

/**
 * Abdeckung als gerundeter Prozentsatz. `null`, wenn sie nicht sinnvoll
 * bestimmbar ist (kein Produkt im Feed) — eine erfundene 0-%-Abdeckung waere
 * schlimmer als gar keine Zahl.
 */
function coveragePercent(benchmark: PriceBenchmarkSummary): number | null {
  if (!benchmark.product_total) return null;
  return Math.round((benchmark.bench_count / benchmark.product_total) * 100);
}

/** Vorzeichenbehaftete Abweichung mit einer Nachkommastelle, oder `--`. */
function medianText(benchmark: PriceBenchmarkSummary, language: string | undefined): string {
  if (benchmark.median_dev_pct === null) return '--';
  const sign = benchmark.median_dev_pct >= 0 ? '+' : '';
  return `${sign}${formatDecimal(benchmark.median_dev_pct, language, 1)} %`;
}

/**
 * Kapitel 2 (Preis) und Kapitel 3 (Ads) als EINE Karte mit zwei Zeilen.
 * Jede Zeile ist fuer sich entscheidbar: ohne Benchmark kein Preis-Aufklapper;
 * die Ads-Zeile bringt ihren Zustand selbst mit.
 */
export function FeedCheckChapters({
  feedId,
  benchmark,
  priceSection,
  adsRow,
  liveRow,
  priceChapterIndex,
  chapterTotal,
  benchmarkFailed = false,
  onRetryBenchmark,
}: FeedCheckChaptersProps) {
  const { t, i18n } = useTranslation('optimization');
  const language = i18n?.language;

  const [priceOpen, setPriceOpen] = useChapterOpen(feedId, 'price');
  const priceContentId = useId();

  const coverage = benchmark ? coveragePercent(benchmark) : null;
  const above = benchmark ? formatInteger(benchmark.above_count, language) : null;
  const median = benchmark ? medianText(benchmark, language) : '--';
  const coverageText = coverage === null ? '--' : `${coverage} %`;

  // Ohne Benchmark ist die Preis-Zeile keine Gliederungsstufe, sondern ein
  // Leersatz mit Ueberschrift. Eine Kapitelnummer daran waere eine Zusage ueber
  // eine Gliederung, die die Seite nicht hat.
  const priceHeading = benchmark && priceChapterIndex && chapterTotal
    ? t('feedCheck.chapters.priceLabel', { index: priceChapterIndex, total: chapterTotal })
    : t('feedCheck.chapters.priceLabelPlain');

  return (
    <Card
      data-testid="feedcheck-chapters"
      style={{ border: '1px solid hsl(var(--primary) / 0.08)', boxShadow: 'var(--shadow-sm)' }}
    >
      <CardContent className="pt-0 pb-0">
        {/* Zeile Preis */}
        <div data-testid="chapter-price" className="flex flex-col gap-2 py-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-sm font-heading font-bold">{priceHeading}</h2>
              {benchmark ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('feedCheck.chapters.priceSummary', {
                    // `count` waehlt die Pluralform, `above` traegt die Anzeige:
                    // i18next interpoliert `{{count}}` roh und schriebe "1180"
                    // ohne Tausenderpunkt (Hausmuster aus dem Feed-Check-Kopf).
                    count: benchmark.above_count,
                    above,
                    median,
                    coverage: coverageText,
                  })}
                </p>
              ) : benchmarkFailed ? (
                <div data-testid="chapter-price-failed" role="status" className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  <span>{t('feedCheck.chapters.priceFailed')}</span>
                  {onRetryBenchmark && (
                    <Button variant="outline" size="sm" className="print:hidden" onClick={onRetryBenchmark}>
                      {t('feedCheck.chapters.retry')}
                    </Button>
                  )}
                </div>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">{t('feedCheck.chapters.priceEmpty')}</p>
              )}
            </div>
            {/* Ein Aufklapper ohne Inhalt ist eine Sackgasse — ohne Benchmark
                gibt es hier deshalb gar keinen Knopf, nur den Leersatz oben. */}
            {benchmark && (
              <button
                type="button"
                className="print:hidden text-sm font-medium text-primary hover:underline shrink-0"
                aria-expanded={priceOpen}
                aria-controls={priceContentId}
                onClick={() => setPriceOpen(!priceOpen)}
              >
                {priceOpen ? t('feedCheck.chapters.close') : t('feedCheck.chapters.open')}
              </button>
            )}
          </div>
          {benchmark && priceOpen && <div id={priceContentId}>{priceSection}</div>}
        </div>

        {/* Zeile Ads: die Kapitel-Zeile mit ihren sechs Zustaenden lebt in
            AdsChapterRow (components/optimization-score/ads), die Seite reicht
            sie fertig herein wie die Preis-Section. Spec 2026-09-09. */}
        {/* `id="ads"` und `id="live"` sind die Ankerziele der Fortschritts-
            leiter (`/#ads`, `/#live`, Spec Abschnitt 6): die Startseite liest
            `location.hash`, klappt das Kapitel auf und scrollt es in den
            `<main>`-Scroller. Sie stehen an den Containern und nicht an den
            Zeilen selbst, damit ein Hash auch dann ein Ziel hat, wenn die
            Zeile gerade ihren Skeleton rendert. */}
        {adsRow && (
          <div id="ads" data-testid="chapter-ads" className="border-t border-border/40 py-5">{adsRow}</div>
        )}
        {/* Zeile "Bei Google": immer NACH Ads. Die Reihenfolge ist die der
            Leiter (erst was wirbt, dann was ausgeliefert wird), und die
            Kapitelnummern der Seite rechnen mit genau dieser Folge. */}
        {liveRow && (
          <div id="live" data-testid="chapter-live" className="border-t border-border/40 py-5">{liveRow}</div>
        )}
      </CardContent>
    </Card>
  );
}
